import React from 'react';
import { useQuery } from '@apollo/client';
import { SHIPMENT_METRICS_QUERY } from '../../services/graphql/queries.js';
import { 
  Package, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  TrendingUp,
  Truck
} from 'lucide-react';

const MetricsCards = ({ userRole }) => {
  const { data, loading, error } = useQuery(SHIPMENT_METRICS_QUERY, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network'
  });

  const metricsData = data?.shipmentMetrics || {
    inTransit: 0,
    completed: 0,
    unapprovedEvents: 0,
    totalShipments: 0
  };

  const cards = [
    {
      title: 'Envíos en Proceso',
      value: metricsData.inTransit,
      icon: Clock,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-900',
      description: 'Envíos actualmente en tránsito',
      trend: loading ? 'Cargando...' : 'Tiempo real'
    },
    {
      title: 'Envíos Finalizados',
      value: metricsData.completed,
      icon: CheckCircle,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-900',
      description: 'Envíos completados exitosamente',
      trend: loading ? 'Cargando...' : 'Actualizados'
    }
  ];

  // Agregar card de estados no aprobados solo para Admin y Transportista
  if (userRole === 'Administrador' || userRole === 'Transportista') {
    cards.push({
      title: 'Estados Pendientes',
      value: metricsData.unapprovedEvents,
      icon: AlertTriangle,
      color: 'bg-red-500',
      bgColor: 'bg-red-50',
      textColor: 'text-red-900',
      description: 'Estados sin aprobar',
      trend: metricsData.unapprovedEvents > 0 ? 'Requieren atención' : 'Todo al día'
    });
  }

  // Card adicional para mostrar total (solo para Admin)
  if (userRole === 'Administrador') {
    cards.push({
      title: 'Total Envíos',
      value: metricsData.totalShipments,
      icon: Package,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-900',
      description: 'Total de envíos en el sistema',
      trend: loading ? 'Cargando...' : 'Sistema completo'
    });
  }

  if (!userRole) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">Cargando métricas del dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">
          Error al cargar las métricas: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-hidden">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {cards.map((card, index) => {
        const Icon = card.icon;
        
        return (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200 h-full min-h-[160px] sm:min-h-[180px] w-full max-w-full"
          >
            <div className="flex flex-col h-full">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className={`${card.bgColor} rounded-lg p-2 sm:p-3`}>
                    <Icon className={`h-5 w-5 sm:h-6 sm:w-6 ${card.color.replace('bg-', 'text-')}`} />
                  </div>
                </div>
                
                <div className="mb-3 sm:mb-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 leading-tight">
                    {card.title}
                  </p>
                  <p className={`text-xl sm:text-2xl font-semibold ${card.textColor} mt-1`}>
                    {card.value !== null ? card.value : '0'}
                  </p>
                </div>
                
                <div className="mt-auto">
                  <p className="text-xs text-gray-500 leading-tight mb-2">
                    {card.description}
                  </p>
                  <div className="flex items-center">
                    {!loading && card.trend === 'Requieren atención' && (
                      <AlertTriangle className="h-3 w-3 text-red-500 mr-1 flex-shrink-0" />
                    )}
                    {!loading && (card.trend === 'Todo al día' || card.trend === 'Actualizados') && (
                      <CheckCircle className="h-3 w-3 text-green-500 mr-1 flex-shrink-0" />
                    )}
                    {loading && (
                      <div className="h-3 w-3 bg-gray-300 rounded-full animate-pulse mr-1 flex-shrink-0"></div>
                    )}
                    <p className={`text-xs leading-tight ${
                      card.trend === 'Requieren atención'
                        ? 'text-red-600' 
                        : card.trend === 'Todo al día' || card.trend === 'Actualizados'
                          ? 'text-green-600'
                          : 'text-gray-500'
                    }`}>
                      {card.trend}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
};

export default MetricsCards;